/**
 * Payments Resource Client
 * Axios-based API client for payment endpoints
 */

import { ApiClient } from './apiClient';
import type { Order } from '../types';

export interface Payment {
    id: string;
    status: string;
    subStatus: string;
    requireAction: 'none' | 'redirect' | 'redirect_to_payment' | 'error' | 'radar' | 'stripe_express_checkout';
    requireActionData?: {
        type: 'redirect' | 'redirect_to_payment' | 'threeds_auth' | 'processor_auth' | 'error' | 'stripe_radar' | 'finix_radar' | 'radar' | 'kesspay_auth' | 'trustflow_auth' | 'mastercard_auth' | 'stripe_express_checkout' | 'ngenius_3ds';
        url?: string;
        processed: boolean;
        processorId?: string;
        metadata?: {
            type: 'redirect' | 'redirect_to_payment' | 'stripe_radar' | 'finix_radar';
            redirect?: {
                redirectUrl: string;
                returnUrl: string;
            };
            threedsSession?: {
                externalSessionId: string;
                acsChallengeUrl: string;
                acsTransID: string;
                messageVersion: string;
            };
            radar?: {
                merchantId?: string;
                environment?: 'sandbox' | 'live';
                orderId?: string;
                publishableKey?: string;
            };
            stripeExpressCheckout?: {
                clientSecret: string;
                publishableKey?: string;
            };
            provider?: string;
            isTest?: boolean;
        };
        redirectUrl?: string;
        resumeToken?: string;
        message?: string;
        errorCode?: string;
    };
    order?: {
        id: string;
        checkoutSessionId: string;
        amount?: number;
        currency?: string;
        [key: string]: unknown;
    };
}

export interface PaymentResponse {
    paymentId: string;
    payment: Payment;
    order?: Order | null;
}

export interface PaymentInstrumentResponse {
    id: string;
    token: string;
    type: string;
    card?: {
        maskedCardNumber?: string;
        expirationMonth?: number;
        expirationYear?: number;
        brand?: string;
    };
}

export interface PaymentInstrumentCustomer {
    id: string;
    type: 'card';
    card: {
        last4: string;
        exp_month: number;
        exp_year: number;
        brand: string;
        bin: string;
    };
    us_bank_account: null;
    sepa_debit: null;
    tokenizer: string;
    token: string;
    isDefault: boolean;
    isActive: boolean;
    metadata: Record<string, unknown>;
    customerId: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
}

export type PaymentInstrumentCustomerResponse = PaymentInstrumentCustomer[];

export interface PaymentOptions {
    enableThreeds?: boolean;
    threedsProvider?: 'basis_theory';
    initiatedBy?: 'customer' | 'merchant';
    source?: 'upsell' | 'checkout' | 'offer' | 'missing_club' | 'forced';

    /**
     * Override the store's default payment flow
     * If not provided, the SDK will auto-read from stepConfig.paymentFlowId (if available)
     * If neither is set, the store's selectedPaymentFlowId will be used
     */
    paymentFlowId?: string;

    /**
     * For alternative payment methods (APM)
     * Processor ID to use for the payment
     */
    processorId?: string;

    /**
     * For alternative payment methods (APM)
     * Payment method type (e.g., 'klarna', 'afterpay', 'paypal')
     */
    paymentMethod?: string;

    /**
     * Shipping rate selected by the customer at checkout. Forwarded to
     * `processPaymentDirect` so the order is created with the right shipping
     * even if the session's stored rate hasn't fully round-tripped or got
     * cleared (race conditions). The backend treats this as authoritative
     * when present, otherwise falls back to the session's stored rate.
     */
    shippingRateId?: string;

    // Legacy callbacks (deprecated - will be removed in future versions)
    /** @deprecated Use onPaymentSuccess instead - this will be removed in v3 */
    onSuccess?: (response: PaymentResponse) => void;
    /** @deprecated Use onPaymentFailed instead - this will be removed in v3 */
    onFailure?: (error: string) => void;
    onRequireAction?: (payment: Payment) => void;

    // Funnel-aligned callbacks (recommended - matches FunnelActionType conditions)
    /**
     * Called when payment succeeds (matches FunnelActionType.PAYMENT_SUCCESS)
     * Use this with useFunnel's next() to navigate to the next step
     * This replaces automatic redirects to /post or /thankyou
     */
    onPaymentSuccess?: (response: PaymentResponse) => void;

    /**
     * Called when payment fails (matches FunnelActionType.PAYMENT_FAILED)
     * Use this to handle retry logic, show errors, or trigger funnel failure flows
     */
    onPaymentFailed?: (error: { code: string; message: string; payment?: Payment }) => void;

    /**
     * Disable automatic redirects to /post or /thankyou (default: true)
     * Set to false only for legacy implementations without funnel orchestrator
     * The funnel orchestrator handles navigation automatically with useFunnel
     */
    disableAutoRedirect?: boolean;
}

export interface CardPaymentMethod {
    cardNumber: string;
    expiryDate: string;
    cvc: string;
    cardholderName?: string;
}

export interface ApplePayToken {
    id: string;
    type: string;
    card: {
        bin: string;
        last4: string;
        expiration_month: number;
        expiration_year: number;
        brand: string;
    };
}

/** Token response from BasisTheory SDK */
export interface BasisTheoryTokenResponse {
    id: string;
    type: string;
    enrichments?: {
        cardDetails?: {
            bin?: string;
            last4?: string;
        };
    };
}

/** External BasisTheory SDK instance shape (from @basis-theory/basis-theory-react) */
export interface BasisTheoryInstance {
    tokens: {
        create(data: Record<string, unknown>): Promise<BasisTheoryTokenResponse>;
    };
}

export interface GooglePayToken {
    id: string;
    type: string;
    card: {
        bin: string;
        last4: string;
        expiration_month: number;
        expiration_year: number;
        brand: string;
    };
    details?: {
        auth_method?: string;
        [key: string]: unknown;
    };
}

export interface PaymentInstrumentInput {
    type: string;
    token?: string;
    card?: {
        maskedCardNumber?: string;
        expirationMonth?: number;
        expirationYear?: number;
        bin?: string;
        last4?: string;
        brand?: string;
    };
    dpanType?: string;
    authMethod?: string;
    provider?: string;
    paymentMethod?: string;
    processorId?: string;
    [key: string]: unknown;
}

export class PaymentsResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Create payment instrument
     */
    async createPaymentInstrument(paymentInstrumentData: PaymentInstrumentInput): Promise<PaymentInstrumentResponse> {
        return this.apiClient.post('/api/v1/payment/create-payment-instrument', {
            paymentInstrumentData,
        });
    }

    /**
     * Create card payment instrument
     */
    async createCardPaymentInstrument(
        basisTheory: BasisTheoryInstance | undefined,
        cardData: CardPaymentMethod
    ): Promise<PaymentInstrumentResponse> {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }

        console.log('Creating card payment instrument');

        // Create token using BasisTheory
        const btResponse = await basisTheory.tokens.create({
            type: 'card',
            data: {
                cvc: cardData.cvc,
                number: Number(cardData.cardNumber.replace(/\s+/g, '')),
                ...this.getCardMonthAndYear(cardData.expiryDate),
            },
            metadata: {
                nonSensitiveField: 'nonSensitiveValue',
            },
        });

        // Create payment instrument through API
        const { expiration_month, expiration_year } = this.getCardMonthAndYear(cardData.expiryDate);
        const paymentInstrumentData = {
            type: btResponse.type,
            card: {
                maskedCardNumber: btResponse.enrichments?.cardDetails?.bin ?
                    `${btResponse.enrichments.cardDetails.bin}****${btResponse.enrichments.cardDetails.last4}` :
                    cardData.cardNumber.replace(/\d(?=\d{4})/g, '*'),
                expirationMonth: expiration_month,
                expirationYear: expiration_year,
            },
            token: btResponse.id,
        };

        const response = await this.apiClient.post(
            '/api/v1/payment/create-payment-instrument',
            { paymentInstrumentData }
        );

        console.log('Payment instrument created:', response);
        return response as PaymentInstrumentResponse;
    }

    /**
     * Create Apple Pay payment instrument
     */
    async createApplePayPaymentInstrument(
        basisTheory: BasisTheoryInstance | undefined,
        applePayToken: ApplePayToken
    ): Promise<PaymentInstrumentResponse> {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }

        if (!applePayToken.id) {
            throw new Error('Apple Pay token is missing');
        }

        const paymentInstrumentData = {
            type: 'apple_pay',
            token: applePayToken.id,
            dpanType: applePayToken.type,
            card: {
                bin: applePayToken.card.bin,
                last4: applePayToken.card.last4,
                expirationMonth: applePayToken.card.expiration_month,
                expirationYear: applePayToken.card.expiration_year,
                brand: applePayToken.card.brand,
            },
        };

        const response = await this.apiClient.post(
            '/api/v1/payment/create-payment-instrument',
            { paymentInstrumentData }
        );

        return response as PaymentInstrumentResponse;
    }

    /**
     * Create Google Pay payment instrument
     */
    async createGooglePayPaymentInstrument(
        basisTheory: BasisTheoryInstance | undefined,
        googlePayToken: GooglePayToken
    ): Promise<PaymentInstrumentResponse> {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }

        if (!googlePayToken.id) {
            throw new Error('Google Pay token is missing');
        }

        const paymentInstrumentData = {
            type: 'google_pay',
            token: googlePayToken.id,
            dpanType: googlePayToken.type,
            card: {
                bin: googlePayToken.card.bin,
                last4: googlePayToken.card.last4,
                expirationMonth: googlePayToken.card.expiration_month,
                expirationYear: googlePayToken.card.expiration_year,
                brand: googlePayToken.card.brand,
            },
            authMethod: googlePayToken?.details?.auth_method,
        };

        const response = await this.apiClient.post(
            '/api/v1/payment/create-payment-instrument',
            { paymentInstrumentData }
        );

        return response as PaymentInstrumentResponse;
    }

    /**
     * Create APM payment instrument (Klarna, Afterpay, PayPal, etc.)
     */
    async createApmPaymentInstrument(
        apmData: {
            provider: string;
            paymentMethod: string;
            processorId: string;
        }
    ): Promise<PaymentInstrumentResponse> {
        console.log('Creating APM payment instrument:', apmData);

        const paymentInstrumentData = {
            type: 'apm',
            provider: apmData.provider,
            paymentMethod: apmData.paymentMethod,
            processorId: apmData.processorId,
        };

        const response = await this.apiClient.post(
            '/api/v1/payment/create-payment-instrument',
            { paymentInstrumentData }
        );

        console.log('APM payment instrument created:', response);
        return response as PaymentInstrumentResponse;
    }

    /**
     * Helper function to format expiry date
     */
    private getCardMonthAndYear(expiryDate: string) {
        const [month, year] = expiryDate.split('/');
        const currentYear = new Date().getFullYear();
        const century = Math.floor(currentYear / 100) * 100;
        const fullYear = Number(year) + century;

        return {
            expiration_month: Number(month),
            expiration_year: fullYear,
        };
    }

    /**
     * Process payment directly with checkout session
     */
    async processPaymentDirect(
        checkoutSessionId: string,
        paymentInstrumentId: string,
        threedsSessionId?: string,
        options: {
            initiatedBy?: 'customer' | 'merchant';
            source?: 'upsell' | 'checkout' | 'offer' | 'missing_club' | 'forced';
            paymentFlowId?: string; // Optional: override the store's default payment flow
            processorId?: string; // For APM payments
            paymentMethod?: string; // For APM payments
            isExpress?: boolean; // True when paying via Stripe Express Checkout Element
            // Shipping rate selected by the customer. Forwarded so the order is
            // created with the right shipping method even if the session's
            // stored rate hasn't fully round-tripped (race) or got cleared.
            // The backend treats this as authoritative when present.
            shippingRateId?: string;
        } = {}
    ): Promise<PaymentResponse> {
        console.log('[PaymentsResource] processPaymentDirect START', options.paymentFlowId ? `(flow: ${options.paymentFlowId})` : '');
        console.log('[PaymentsResource] Full options object:', JSON.stringify(options, null, 2));
        console.log('[PaymentsResource] processorId:', options.processorId, 'paymentMethod:', options.paymentMethod);
        try {
            const requestBody = {
                checkoutSessionId,
                paymentInstrumentId,
                ...(threedsSessionId && { threedsSessionId }),
                ...(options.initiatedBy && { initiatedBy: options.initiatedBy }),
                ...(options.source && { source: options.source }),
                ...(options.paymentFlowId && { paymentFlowId: options.paymentFlowId }),
                ...(options.processorId && { processorId: options.processorId }),
                ...(options.paymentMethod && { paymentMethod: options.paymentMethod }),
                ...(options.isExpress && { isExpress: options.isExpress }),
                ...(options.shippingRateId && { shippingRateId: options.shippingRateId }),
            };
            console.log('[PaymentsResource] Request body being sent:', JSON.stringify(requestBody, null, 2));
            const response = await this.apiClient.post<PaymentResponse>('/api/public/v1/checkout/pay-v2', requestBody);
            console.log('[PaymentsResource] processPaymentDirect SUCCESS:', response);
            return response;
        } catch (error) {
            console.error('[PaymentsResource] processPaymentDirect ERROR:', error);
            throw error;
        }
    }

    /**
     * Get card payment instruments for customer
     */
    async getCardPaymentInstruments(): Promise<PaymentInstrumentCustomerResponse> {
        return this.apiClient.get('/api/v1/payment-instruments/customer');
    }

    /**
     * Mark payment action as processed
     */
    async markPaymentActionProcessed(paymentId: string): Promise<void> {
        return this.apiClient.post('/api/v1/payments/require-action/processed', {
            paymentId,
        });
    }

    /**
     * Get payment status
     */
    async getPaymentStatus(paymentId: string): Promise<Payment> {
        return this.apiClient.get(`/api/v1/payments/${paymentId}`);
    }

    /**
     * Retrieve payment status from processor
     * Used for external payment flows that require server-side status checks
     */
    async retrievePayment(paymentId: string): Promise<{
        retrieveResult?: {
            status?: string;
            message?: string;
            success?: boolean;
        };
        paymentId: string;
        transactionCreated?: boolean;
        status?: string;
        transactionId?: string;
        message?: string;
        success?: boolean;
        error?: string;
    }> {
        return this.apiClient.post('/api/v1/payments/retrieve', {
            paymentId,
        });
    }

    async saveRadarSession(data: {
        paymentId?: string;
        orderId?: string;
        checkoutSessionId?: string;
        finixRadarSessionId?: string;
        finixRadarSessionData?: Record<string, unknown>;
        stripeRadarSessionId?: string;
        stripeRadarSessionData?: Record<string, unknown>;
        airwallexRadarSessionId?: string;
    }): Promise<{ success: boolean; radarSessionId: string }> {
        return this.apiClient.post('/api/v1/radar-sessions', data);
    }

    /**
     * Complete payment after an action (3DS, radar, etc.)
     * This resumes the payment flow that was paused for additional authentication/verification
     */
    async completePaymentAfterAction(paymentId: string): Promise<Payment> {
        return this.apiClient.post('/api/v1/payments/complete-after-3ds', {
            paymentId,
        });
    }

    /**
     * Update 3DS session status (used for Airwallex 3DS return handling)
     */
    async updateThreedsStatus(data: {
        paymentId: string;
        status: 'succeeded' | 'failed' | 'pending';
        paymentIntentId: string;
    }): Promise<Payment> {
        return this.apiClient.post('/api/v1/threeds/status', data);
    }

    /**
     * Complete N-Genius payment after WebSDK 3DS flow finishes.
     * Verifies state from N-Genius server-side and updates the payment record.
     */
    async ngeniusThreedsComplete(data: {
        paymentId: string;
        orderReference: string;
        paymentReference: string;
    }): Promise<Payment> {
        return this.apiClient.post('/api/v1/payments/ngenius/threeds-complete', data);
    }
}
